import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hk_customer_token";

const CustomerAuthContext = createContext({
  user: null,       // null when loading, object when authed, false when unauthenticated
  loading: true,
  signup: async () => {},
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
});

/** Format FastAPI error responses (arrays + objects) into a printable string. */
export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

/** axios instance that attaches the customer bearer token on every request. */
export const customerApi = axios.create({ baseURL: API });
customerApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const CustomerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);   // null = checking
  const [loading, setLoading] = useState(true);

  const setToken = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  };

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await customerApi.get("/customer/me");
      setUser(data);
    } catch (e) {
      // token invalid or expired
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signup = async ({ name, email, mobile, password }) => {
    const { data } = await customerApi.post("/customer/signup", { name, email, mobile, password });
    setToken(data.token);
    setUser(data.customer);
    return data.customer;
  };

  const login = async ({ identifier, password }) => {
    const { data } = await customerApi.post("/customer/login", { identifier, password });
    setToken(data.token);
    setUser(data.customer);
    return data.customer;
  };

  const logout = () => {
    setToken(null);
    setUser(false);
  };

  return (
    <CustomerAuthContext.Provider value={{ user, loading, signup, login, logout, refresh }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);
